import {isBeddingSelection,beddingLabel,type BeddingSelection} from './blissBedding.js'
import { isWallAppearances } from './blissWallAppearance.js'
import { BLISS_ALL_FINISH_OPTIONS, isBasicPaintColor, labelForFinish, type BlissBasicFinishId, type BlissBasicFinishTreatment } from './blissBasicConfigurator.js'

export type DrawerFaceStyle = 'shiplap' | 'shaker' | 'flat_pull'
export type SurfaceFaceStyle = 'nickel_gap' | 'shaker' | 'flat'
export const SURFACE_FACE_STYLES: {id:SurfaceFaceStyle;label:string}[] = [
  {id:'nickel_gap',label:'Nickel gap'}, {id:'shaker',label:'Shaker'}, {id:'flat',label:'Flat'},
]
export const DRAWER_FACE_STYLES: {id: DrawerFaceStyle; label: string}[] = [
  {id:'shiplap',label:'Nickel-gap / shiplap · semi-flush'},
  {id:'shaker',label:'Shaker · recessed panel and trim'},
  {id:'flat_pull',label:'Flat'},
]

export type BedTrimStyle = 'none' | 'cap' | 'cap_quarter_round'
export type CrownMoldingStyle = 'none' | 'crown_7'
export type DrawerPullStyle = 'cutout' | 'gold_bar' | 'silver_bar' | 'rope'
export const BED_TRIM_OPTIONS: {id: BedTrimStyle; label: string}[] = [
  {id:'none', label:'Standard bed edges'},
  {id:'cap', label:'Projecting cap trim'},
  {id:'cap_quarter_round', label:'Cap trim + quarter-round base'},
]
export const DRAWER_PULL_OPTIONS: {id: DrawerPullStyle; label: string}[] = [
  {id:'cutout', label:'Hand-pull holes'},
  {id:'gold_bar', label:'Gold bar pulls'},
  {id:'silver_bar', label:'Silver bar pulls'},
  {id:'rope', label:'Rope pulls'},
]
/** Explicit Custom trim and hardware choices also route standard layouts to Custom. */
export function hasCustomDetailUpgrades(finish?: BlissRoomFinish): boolean {
  return !!finish && ((finish.bedTrim !== undefined && finish.bedTrim !== 'none') || finish.crownMolding === 'crown_7' || finish.drawerPullStyle !== undefined)
}


export type MattressThickness = { upper?: number; lower?: number }
export function mattressThickness(value: MattressThickness | undefined, level: "upper" | "lower", fallback=6): number {
  const n=value?.[level]
  return typeof n === "number" && Number.isFinite(n) ? Math.min(16,Math.max(4,n)) : fallback
}

export type BlissRoomFinish = {
  bedding?: BeddingSelection
  stringerFaceStyle?: SurfaceFaceStyle
  upperBunkFaceStyle?: SurfaceFaceStyle
  wallAppearance?: import('./blissWallAppearance').WallAppearances
  mattressThickness?: MattressThickness
  bedTrim?: BedTrimStyle
  crownMolding?: CrownMoldingStyle
  drawerPullStyle?: DrawerPullStyle
  drawerFaceStyle?: DrawerFaceStyle
  treatment: BlissBasicFinishTreatment
  frameId: BlissBasicFinishId
  railColors?: Partial<Record<1 | 2 | 3, BlissBasicFinishId>>
  topRailId?: BlissBasicFinishId
  accentId: BlissBasicFinishId
}

export const BLISS_FINISH_COLORS: Record<BlissBasicFinishId, string> = {
  sand: '#d7c5a5',
  taupe: '#a58f7b',
  warm_gray: '#aaa39a',
  sage: '#9aa58b',
  olive: '#727653',
  forest: '#384e42',
  terracotta: '#ba7257',
  clay: '#b99a89',
  slate_blue: '#657e8b',
  charcoal: '#454746',
  white_oak: '#c8ad80',
  walnut: '#76523b',
  red: '#b5262d',
  blue: '#2454a4',
  gloss_white: '#ffffff',
  matte_white: '#ffffff',
  white: '#ffffff',
  black: '#242424',
  off_white: '#f2eee3',
  espresso: '#3d2918',
  navy: '#1e3348',
  natural_maple: '#c9955c',
}

export const DEFAULT_ROOM_FINISH: BlissRoomFinish = {
  treatment: 'painted_stained', frameId: 'matte_white', accentId: 'matte_white',
}

/** Stored Custom colors remain valid, but cannot be sold as a Basic paint finish. */
export function requiresCustomFinish(finish?: BlissRoomFinish): boolean {
  return (!!finish?.railColors && Object.values(finish.railColors).some(id => !isBasicPaintColor(id))) || hasCustomDetailUpgrades(finish) || (!!finish && (finish.treatment === 'unfinished_raw'
    || !isBasicPaintColor(finish.frameId) || !isBasicPaintColor(finish.accentId) || (finish.topRailId !== undefined && !isBasicPaintColor(finish.topRailId))))
}

export function isBlissRoomFinish(value: unknown): value is BlissRoomFinish {
  if (!value || typeof value !== 'object') return false
  const f = value as BlissRoomFinish
  return ['painted_stained', 'unfinished_raw'].includes(f.treatment)
    && (f.bedding === undefined || isBeddingSelection(f.bedding))
    && (f.stringerFaceStyle === undefined || SURFACE_FACE_STYLES.some(s=>s.id===f.stringerFaceStyle))
    && (f.upperBunkFaceStyle === undefined || SURFACE_FACE_STYLES.some(s=>s.id===f.upperBunkFaceStyle))
    && (f.wallAppearance === undefined || isWallAppearances(f.wallAppearance))
    && BLISS_ALL_FINISH_OPTIONS.some(o => o.id === f.frameId)
    && BLISS_ALL_FINISH_OPTIONS.some(o => o.id === f.accentId)
    && (f.mattressThickness === undefined || (!!f.mattressThickness && typeof f.mattressThickness === 'object' && !Array.isArray(f.mattressThickness) && Object.entries(f.mattressThickness).every(([key,n]) => ['upper','lower'].includes(key) && typeof n === 'number' && Number.isFinite(n) && n>=4 && n<=16)))
    && (f.railColors === undefined || (!!f.railColors && typeof f.railColors === 'object' && !Array.isArray(f.railColors) && Object.entries(f.railColors).every(([level,id]) => ['1','2','3'].includes(level) && BLISS_ALL_FINISH_OPTIONS.some(o => o.id === id))))
    && (f.topRailId === undefined || BLISS_ALL_FINISH_OPTIONS.some(o => o.id === f.topRailId))
    && (f.bedTrim === undefined || BED_TRIM_OPTIONS.some(o => o.id === f.bedTrim))
    && (f.crownMolding === undefined || f.crownMolding === 'none' || f.crownMolding === 'crown_7')
    && (f.drawerPullStyle === undefined || DRAWER_PULL_OPTIONS.some(o => o.id === f.drawerPullStyle))
    && (f.drawerFaceStyle === undefined || DRAWER_FACE_STYLES.some(o => o.id === f.drawerFaceStyle))
}

export function roomFinishColors(finish: BlissRoomFinish) {
  return finish.treatment === 'unfinished_raw'
    ? { frame: '#e2cea8', accent: '#e2cea8', topRail: '#e2cea8' }
    : { frame: BLISS_FINISH_COLORS[finish.frameId], accent: BLISS_FINISH_COLORS[finish.accentId], topRail: BLISS_FINISH_COLORS[finish.topRailId ?? finish.accentId] }
}

export function roomFinishDescription(finish: BlissRoomFinish): string {
  const details = `${finish.bedTrim && finish.bedTrim !== 'none' ? ' · Bed trim: ' + BED_TRIM_OPTIONS.find(o => o.id === finish.bedTrim)!.label : ''}${finish.drawerPullStyle ? ' · Drawer pulls: ' + DRAWER_PULL_OPTIONS.find(o => o.id === finish.drawerPullStyle)!.label : ''}${hasCustomDetailUpgrades(finish) ? ' · Custom trim & hardware selected' : ''}`
  const rails = Object.entries(finish.railColors ?? {}).map(([level,id]) => ` · Rail ${level}: ${labelForFinish(id!)}`).join('')
  const surfaces = `${finish.bedding ? ` · Bedding preview (not included): upper ${beddingLabel(finish.bedding.upper)}, lower ${beddingLabel(finish.bedding.lower)}` : ''}${finish.stringerFaceStyle ? ' · Stringer faces: ' + SURFACE_FACE_STYLES.find(s=>s.id===finish.stringerFaceStyle)!.label : ''}${finish.upperBunkFaceStyle ? ' · Upper bunk faces: ' + SURFACE_FACE_STYLES.find(s=>s.id===finish.upperBunkFaceStyle)!.label : ''}`
  const face = ` · Drawer faces: ${DRAWER_FACE_STYLES.find(o => o.id === (finish.drawerFaceStyle ?? 'shaker'))!.label}` + details + (finish.crownMolding === 'crown_7' ? ' · 7″ crown molding at cabin tops · matches frame finish · estimated in package pricing' : '') + rails + (finish.mattressThickness ? ` · Mattresses: upper ${mattressThickness(finish.mattressThickness,'upper')}″ / lower ${mattressThickness(finish.mattressThickness,'lower')}″` : '')
  if (finish.treatment === 'unfinished_raw') return 'Unfinished / raw wood' + face + surfaces
  const label = labelForFinish
  return `Frame: ${label(finish.frameId)} · Guardrails, rungs & treads: ${label(finish.accentId)}${finish.topRailId ? ` · Top rails: ${label(finish.topRailId)}` : ''}` + face + surfaces
}

/** Flat customer upgrade per room; not a raw-material cost. Stains are separate. */
export const CUSTOM_PAINT_UPCHARGE = 2000
export function customPaintUpcharge(finish?: BlissRoomFinish): number {
  return finish?.treatment === 'painted_stained' &&
    [finish.frameId, finish.accentId, finish.topRailId, ...Object.values(finish.railColors ?? {})].some(id => !!id && !isBasicPaintColor(id) && !['white', 'natural_maple', 'espresso', 'white_oak', 'walnut'].includes(id))
    ? CUSTOM_PAINT_UPCHARGE : 0
}


